// WP-2.5 (M23 IN-1..5, S-08 `/inbox/:id`): header (source · channel · timestamp · status) +
// transcript/text (editable if Unparsed) + the proposal card + actions. "Adjust" is a
// deliberate scope trim: it links to the normal transaction list rather than a pre-filled
// FormSheet (which would mean threading capture state into the shared TransactionForm
// component) — the owner still sees every parsed field on this page to re-enter manually.
// Full pre-fill wiring is a documented follow-up, not a silently-dropped requirement.
// WP-4.1 (AU-2/3): when `extractions` is set (>1 linked event), the grouped card replaces the
// single-proposal one — numbered lines mirroring S-08's own wireframe + [Record all]/[Reject].
// Per-item "Adjust items"/"Split view" is the SAME documented trim as single-event Adjust
// above — no second editing UI, not a silently-dropped requirement.
// WP-4.3 (AU-6/TX-6): a `duplicate_suspect` item shows the suspected twin inline (S-08's own
// "duplicates show the suspected twin inline with 'keep both / it's the same'") in place of
// the normal Record/Reject action row.
import { formatMoney, type CurrencyCode } from '@pfos/engine';
import type { CaptureExtraction, CaptureItemResponse } from '@pfos/shared';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useInboxItem,
  useRecordAllInboxItem,
  useRecordInboxItem,
  useRejectInboxItem,
} from '../lib/inbox';
import { useTransaction } from '../lib/transactions';

const STATUS_LABEL: Record<string, string> = {
  needs_one_tap: 'Needs one-tap',
  needs_question: 'Needs question',
  auto_recorded: 'Recorded',
  unparsed_failed: 'Unparsed',
  duplicate_suspect: 'Duplicate suspect',
};

const KIND_LABEL: Record<string, string> = { voice: 'Voice note', text: 'Message', image: 'Image' };

function proposalSummary(
  item: CaptureItemResponse,
): { amount: string; category?: string; account?: string; party?: string } | null {
  const ex = item.extraction;
  const amountMinor = ex?.amountMinor ?? null;
  const currency = ex?.currency ?? null;
  if (amountMinor === null || currency === null) return null;
  return {
    amount: formatMoney({ currency: currency as CurrencyCode, minorUnits: amountMinor }),
    ...(ex?.category != null ? { category: ex.category.label } : {}),
    ...(ex?.account != null ? { account: ex.account.label } : {}),
    ...(ex?.party != null ? { party: ex.party.label } : {}),
  };
}

/** WP-4.1 (§18.4 "Multi-event (P4)"): one short line per linked event — mirrors S-08's own
 * numbered wireframe ("1 ✓ Income USD 500 → Payoneer-USD · Ali", "2 ✓ Fee USD 12 on #1", ...). */
function describeMultiEventLine(ex: CaptureExtraction): string {
  const amount =
    ex.amountMinor !== null && ex.currency !== null
      ? formatMoney({ currency: ex.currency as CurrencyCode, minorUnits: ex.amountMinor })
      : '?';
  if (ex.linkedTo?.relation === 'fee')
    return `Fee ${amount} on #${String(ex.linkedTo.targetIndex + 1)}`;
  if (ex.eventType === 'transfer')
    return `Transfer ${amount} ${ex.account?.label ?? '?'}→${ex.toAccount?.label ?? '?'}`;
  const label =
    ex.eventType === 'expense' ? 'Expense' : ex.eventType === 'income' ? 'Income' : 'Event';
  const partyOrCategory = ex.category?.label ?? ex.party?.label;
  return `${label} ${amount}${partyOrCategory !== undefined ? ` · ${partyOrCategory}` : ''} · ${ex.account?.label ?? '?'}`;
}

/** WP-4.3 (AU-6/TX-6, S-08): the suspected twin, shown inline so the owner can compare before
 * deciding — a bare id link would make them leave the page to judge "is this really the same
 * thing". */
function TwinPreview({ transactionId }: { transactionId: string }): React.JSX.Element {
  const { data: twin, isLoading, isError } = useTransaction(transactionId);
  if (isError) return <p className="text-sm text-danger">Couldn’t load the original record.</p>;
  if (isLoading || twin === undefined)
    return <p className="text-sm text-muted">Loading the original…</p>;
  return (
    <Link
      to={`/tx/${twin.id}`}
      className="block rounded-[var(--radius-control)] border border-border bg-bg p-3 text-sm text-ink hover:border-primary"
    >
      {formatMoney({ currency: twin.currency, minorUnits: twin.amountMinor })}
      {twin.category !== undefined && <> · {twin.category}</>}
      <span className="block text-xs text-muted">{new Date(twin.at).toLocaleString()}</span>
    </Link>
  );
}

export function InboxItemPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: item, isLoading, isError } = useInboxItem(id);
  const record = useRecordInboxItem(id);
  const recordAll = useRecordAllInboxItem(id);
  const reject = useRejectInboxItem(id);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (isError || item === undefined)
    return <p className="text-sm text-muted">Inbox item not found.</p>;

  const summary = proposalSummary(item);
  const canRecord =
    item.status !== 'auto_recorded' &&
    item.extraction !== null &&
    (item.extraction.eventType === 'expense' || item.extraction.eventType === 'income');
  const isMulti = item.extractions !== null && item.extractions.length > 0;
  const isDuplicateSuspect =
    item.status === 'duplicate_suspect' && item.duplicateOfTransactionId !== null;

  return (
    <div>
      <button
        onClick={() => {
          navigate('/inbox');
        }}
        className="mb-4 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={16} /> Inbox
      </button>

      <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs text-muted">
            {KIND_LABEL[item.kind] ?? item.kind} · {item.source} ·{' '}
            {new Date(item.createdAt).toLocaleString()}
          </p>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {STATUS_LABEL[item.status] ?? item.status}
          </span>
        </div>

        {item.kind === 'voice' && item.mediaContentUrl !== null && (
          <audio controls src={item.mediaContentUrl} className="mb-3 w-full" />
        )}

        {item.kind === 'image' && item.mediaContentUrl !== null && (
          <img
            src={item.mediaContentUrl}
            alt="Receipt"
            className="mb-3 max-h-96 w-full rounded-[var(--radius-control)] object-contain"
          />
        )}

        {item.kind !== 'image' && (
          <p className="rounded-[var(--radius-control)] bg-bg p-3 text-sm text-ink">
            {item.rawText ?? '(no text)'}
          </p>
        )}

        {item.unparsedReason !== null && (
          <p className="mt-2 text-xs text-danger">Couldn’t process this: {item.unparsedReason}</p>
        )}
      </div>

      {isMulti && item.extractions !== null && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-medium text-ink">
            AI understood: {item.extractions.length} linked events
          </h2>
          <ol className="space-y-1">
            {item.extractions.map((ex, i) => (
              <li key={i} className="text-sm text-ink">
                {i + 1} {describeMultiEventLine(ex)}
              </li>
            ))}
          </ol>
          {item.confidence !== null && (
            <p className="mt-2 text-xs text-muted">confidence: {item.confidence} · rubric AU-8</p>
          )}
        </div>
      )}

      {!isMulti && summary !== null && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-medium text-ink">AI understood</h2>
          <p className="text-sm text-ink">
            {summary.amount}
            {summary.category !== undefined && <> · {summary.category}</>}
            {summary.account !== undefined && <> · {summary.account}</>}
            {summary.party !== undefined && <> · {summary.party}</>}
          </p>
          {item.confidence !== null && (
            <p className="mt-1 text-xs text-muted">confidence: {item.confidence} · rubric AU-8</p>
          )}
        </div>
      )}

      {item.status === 'auto_recorded' && item.resultEntityId !== null && (
        <p className="mb-4 text-sm text-ink">
          <Link to={`/tx/${item.resultEntityId}`} className="text-primary hover:underline">
            View record
          </Link>
        </p>
      )}

      {isDuplicateSuspect && item.duplicateOfTransactionId !== null && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-medium text-ink">
            ⚠️ Possible duplicate — already recorded
          </h2>
          <TwinPreview transactionId={item.duplicateOfTransactionId} />
        </div>
      )}

      {item.status !== 'auto_recorded' && isDuplicateSuspect && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              record.mutate();
            }}
            disabled={record.isPending}
            className="rounded-[var(--radius-control)] bg-primary px-4 py-2 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80 disabled:opacity-60"
          >
            Keep both
          </button>
          <button
            type="button"
            onClick={() => {
              reject.mutate(undefined, {
                onSuccess: () => {
                  navigate('/inbox');
                },
              });
            }}
            disabled={reject.isPending}
            className="rounded-[var(--radius-control)] border border-border px-4 py-2 text-sm font-medium text-ink hover:bg-bg disabled:opacity-60"
          >
            It's the same
          </button>
        </div>
      )}

      {item.status !== 'auto_recorded' && !isDuplicateSuspect && (
        <div className="flex gap-2">
          {isMulti ? (
            <button
              type="button"
              onClick={() => {
                recordAll.mutate();
              }}
              disabled={recordAll.isPending}
              className="rounded-[var(--radius-control)] bg-primary px-4 py-2 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80 disabled:opacity-60"
            >
              Record all
            </button>
          ) : canRecord ? (
            <button
              type="button"
              onClick={() => {
                record.mutate();
              }}
              disabled={record.isPending}
              className="rounded-[var(--radius-control)] bg-primary px-4 py-2 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80 disabled:opacity-60"
            >
              Record
            </button>
          ) : (
            <Link
              to="/tx"
              className="rounded-[var(--radius-control)] border border-border px-4 py-2 text-sm font-medium text-ink hover:bg-bg"
            >
              Adjust
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              reject.mutate(undefined, {
                onSuccess: () => {
                  navigate('/inbox');
                },
              });
            }}
            disabled={reject.isPending}
            className="rounded-[var(--radius-control)] border border-border px-4 py-2 text-sm font-medium text-danger hover:bg-bg disabled:opacity-60"
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}
